import { useCallback, useEffect, useMemo, useRef } from 'react';
import { SopAPI } from '../../../../utils/api';
import {
  collectScopedVariables,
  resolveVariableExpanded,
  nextVariableColor,
  generateVariableId,
} from '../../../../utils/variableScope';
import { buildVariableOfType } from '../../../../utils/variableTypeChange';

/**
 * Doc-scoped variables plus the campaign chain (highest level wins).
 * Panel already idle-debounces keystrokes — persist immediately here.
 */
export function useLibraryDocVariables({
  docId,
  docVariables,
  setDocVariables,
  setVariablesColumnWidths,
  variableCampaign,
  allSprints,
  missionVariables = null,
}) {
  const varsTimerRef = useRef(null);

  const scopedVariables = useMemo(
    () =>
      collectScopedVariables({
        docVariables,
        campaign: variableCampaign,
        allSprints,
        missionVariables,
      }),
    [docVariables, variableCampaign, allSprints, missionVariables]
  );

  const resolveVariable = useCallback(
    (name) => resolveVariableExpanded(scopedVariables, name),
    [scopedVariables]
  );

  const handleDocVariablesChange = useCallback(
    (vars) => {
      setDocVariables(vars);
      clearTimeout(varsTimerRef.current);
      varsTimerRef.current = null;
      SopAPI.update(docId, { variables: vars }).catch(() => {});
    },
    [docId, setDocVariables]
  );

  const handleVariablesColumnWidthsChange = useCallback(
    (widths) => {
      setVariablesColumnWidths(widths);
      SopAPI.update(docId, { variablesColumnWidths: widths }).catch(() => {});
    },
    [docId, setVariablesColumnWidths]
  );

  useEffect(() => () => clearTimeout(varsTimerRef.current), []);

  const handleCreateVariable = useCallback(
    (name, type) => {
      setDocVariables((prev) => {
        const next = [
          ...prev,
          buildVariableOfType(
            { id: generateVariableId(), name, color: nextVariableColor(prev) },
            type
          ),
        ];
        clearTimeout(varsTimerRef.current);
        varsTimerRef.current = null;
        SopAPI.update(docId, { variables: next }).catch(() => {});
        return next;
      });
    },
    [docId, setDocVariables]
  );

  return {
    scopedVariables,
    resolveVariable,
    handleDocVariablesChange,
    handleVariablesColumnWidthsChange,
    handleCreateVariable,
  };
}
